import { Pipe, PipeTransform } from '@angular/core';
const CODE_OR_MATH =
  /(```[\s\S]*?```|~~~[\s\S]*?~~~|```[\s\S]*$|~~~[\s\S]*$|`+[^`\n]*`+)|\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]|\\\(([\s\S]+?)\\\)|(\\begin\{(align|alignat|equation|gather|CD)\*?\}[\s\S]+?\\end\{\6\*?\})|\$(?![\s$])([^\n$]*[^\s$])\$/g;

// Everything marked's escape rule accepts, minus `$` — a `$` inside a body would confuse
// auto-render's own delimiter scan, so it is left exactly as it came in.
const MARKDOWN_PUNCTUATION = /([!"#%&'()*+,\-.\/:;<=>?@[\]^_`{|}~])/g;

@Pipe({
  name: 'parseMarkdown'
})
export class ParseMarkdownPipe implements PipeTransform {

  transform(value: string): unknown {
    if (value == undefined || value == null || value.trim() === '')
      return '';

    const decoded = value
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    let result = '';
    let cursor = 0;
    let match: RegExpExecArray | null;
    CODE_OR_MATH.lastIndex = 0;

    while ((match = CODE_OR_MATH.exec(decoded)) !== null) {
      const [, code, dollars, bracket, paren, env, , inline] = match as unknown as (string | undefined)[];
      result += this.escapeProse(decoded.slice(cursor, match.index));

      if (code !== undefined) {
        // Code is verbatim by definition: no math, no escaping. Backslash escapes and inline
        // HTML are both inert inside a fence or a code span, so anything added here would show
        // up on screen as literal characters.
        result += code;
      } else {
        const block = dollars ?? bracket ?? env;
        const body = block ?? paren ?? inline ?? '';
        result += block !== undefined
          ? `$$${this.shieldMath(body)}$$`
          : `$${this.shieldMath(body)}$`;
      }
      cursor = CODE_OR_MATH.lastIndex;
    }

    return result + this.escapeProse(decoded.slice(cursor));
  }
  private escapeProse(text: string): string {
    return text
      .split('\n')
      .map((line, i) => i > 0 && /^(?: {4}|\t)/.test(line)
        ? line
        : line
          .replace(/~/g, '\\~')
          .replace(/\$/g, '<span class="md-literal-dollar">$</span>'))
      .join('\n');
  }
  private shieldMath(expr: string): string {
    return expr
      // A LaTeX % comment runs to end of line, so it has to go before the newlines do, otherwise
      // it would swallow the rest of the expression. `\%` is a percent sign, not a comment.
      .replace(/(^|[^\\])%[^\n]*/g, '$1')
      // Newlines are just whitespace to KaTeX — `\\` is the row break, not a line break — but to
      // marked a line starting with `+`, `-`, `#`, `>` or `1.` is a list/heading/quote that
      // interrupts the paragraph and tears the math region in half (a multi-line `\boxed{...}`
      // whose terms start with `+` came out as a bullet list). Two trailing spaces would become a
      // <br>, and a blank line would split the region across two <p>. One line removes them all.
      .replace(/\s*\n\s*/g, ' ')
      // Order matters: doubling first means marked's `\\` -> `\` restores the original
      // backslashes, and the escapes added next are consumed as escapes rather than doubled.
      .replace(/\\/g, '\\\\')
      .replace(MARKDOWN_PUNCTUATION, '\\$1')
      .trim();
  }

}
